import { useEffect, useMemo, useState } from "react";
import { Download, FileText } from "lucide-react";
import api from "../lib/api";
import { money } from "../lib/format";

function firstOfMonth() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function ReportsPage() {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/summary", { params: { start, end } });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const totals = data?.totals || { income: 0, expense: 0, net: 0, count: 0 };
  const cats = data?.categories || [];
  const daily = data?.daily || [];

  const maxDaily = useMemo(() => {
    if (!daily.length) return 1;
    return Math.max(...daily.map((d) => Math.max(d.income, d.expense))) || 1;
  }, [daily]);

  const downloadCSV = () => {
    if (!data) return;
    const rows = [["Tarih", "Gelir", "Gider", "Net"]];
    daily.forEach((d) => rows.push([d.date, d.income, d.expense, d.income - d.expense]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `verafin-rapor-${start}-${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">RAPORLAR</div>
          <h1>Finansal Raporlar</h1>
          <p>Belirli bir aralıktaki nakit akışınızı, kategori dağılımınızı ve trendleri inceleyin.</p>
        </div>
        <div className="head-actions">
          <button className="secondary-btn" onClick={downloadCSV} disabled={!daily.length} data-testid="report-download-csv">
            <Download size={16} /> CSV indir
          </button>
        </div>
      </div>

      <div className="panel filter-panel" data-testid="report-filters">
        <div className="form-grid three">
          <label>
            Başlangıç
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" />
          </label>
          <label>
            Bitiş
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" />
          </label>
          <div className="filter-actions">
            <button className="primary-btn" onClick={load} data-testid="apply-report-filters">
              Uygula
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Rapor hazırlanıyor…</div>
      ) : (
        <>
          <section className="metric-grid">
            <div className="metric" data-testid="report-income">
              <div className="metric-label">Toplam gelir</div>
              <div className="metric-value income-text">{money(totals.income)}</div>
            </div>
            <div className="metric" data-testid="report-expense">
              <div className="metric-label">Toplam gider</div>
              <div className="metric-value expense-text">{money(totals.expense)}</div>
            </div>
            <div className="metric" data-testid="report-net">
              <div className="metric-label">Net</div>
              <div className="metric-value">{money(totals.net)}</div>
            </div>
            <div className="metric" data-testid="report-count">
              <div className="metric-label">İşlem sayısı</div>
              <div className="metric-value">{totals.count}</div>
            </div>
          </section>

          <section className="dashboard-grid">
            <div className="panel">
              <div className="panel-head">
                <div>
                  <h2>Günlük nakit akışı</h2>
                  <span>Seçilen aralık</span>
                </div>
              </div>
              {daily.length === 0 ? (
                <div className="empty-inline">
                  <strong>Kayıt yok</strong>
                  <span>Bu aralıkta işlem bulunmuyor.</span>
                </div>
              ) : (
                <div className="bar-chart" data-testid="report-bar-chart">
                  {daily.slice(-30).map((d) => (
                    <div className="bar-group" key={d.date} title={d.date}>
                      <div className="bar income" style={{ height: `${(d.income / maxDaily) * 100}%` }} />
                      <div className="bar expense" style={{ height: `${(d.expense / maxDaily) * 100}%` }} />
                    </div>
                  ))}
                </div>
              )}
              <div className="chart-legend">
                <span><i style={{ background: "#10b981" }} /> Gelir</span>
                <span><i style={{ background: "#f59e0b" }} /> Gider</span>
              </div>
            </div>

            <div className="panel">
              <div className="panel-head">
                <div>
                  <h2>Kategori dağılımı</h2>
                  <span>Toplam tutara göre</span>
                </div>
              </div>
              {cats.length === 0 ? (
                <div className="empty-inline">
                  <FileText size={20} />
                  <strong>Kayıt yok</strong>
                </div>
              ) : (
                <div className="category-list">
                  {cats.slice(0, 8).map((c, i) => {
                    const max = cats[0].amount || 1;
                    return (
                      <div className="cat-row" key={`${c.type}:${c.category}`} data-testid={`report-cat-${i}`}>
                        <div className="cat-head">
                          <span className={c.type === "income" ? "income-text" : "expense-text"}>
                            {c.type === "income" ? "Gelir" : "Gider"}
                          </span>
                          <strong>{c.category}</strong>
                          <span>{money(c.amount)}</span>
                        </div>
                        <div className="cat-bar-track">
                          <div
                            className={`cat-bar ${c.type}`}
                            style={{ width: `${(c.amount / max) * 100}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </>
  );
}
